import { FormEvent, useCallback, useEffect, useState } from 'react'
import classes from './ChatWidget.module.scss'
import FeatureModal, { ModalFeedback, ModalLoading } from './FeatureModal'
import { ChatCompanyListApi } from './chatService'
import { normalizeCompanies } from './chatMappers'
import { displayValue, formatChatDate, getChatErrorMessage } from './chatFormatters'
import { ChatCompany, ChatRequestHandler, SerialNumberCheck } from './types'

type SearchMode = 'serialNumber' | 'invoiceNumber' | 'eInvoiceNumber' | 'dateRange'

const SEARCH_MODES: Array<{ key: SearchMode; label: string; placeholder?: string }> = [
  { key: 'serialNumber', label: 'Seri no', placeholder: 'Seri numarasını girin' },
  { key: 'invoiceNumber', label: 'Fatura no', placeholder: 'Fatura numarasını girin' },
  { key: 'eInvoiceNumber', label: 'E-fatura no', placeholder: 'E-fatura numarasını girin' },
  { key: 'dateRange', label: 'Tarih aralığı' },
]

interface Props {
  onClose: () => void
  onRequest: ChatRequestHandler
}

export default function SerialNumberCheckModal({ onClose, onRequest }: Props) {
  const [companies, setCompanies] = useState<ChatCompany[]>([])
  const [companiesLoading, setCompaniesLoading] = useState(true)
  const [companyError, setCompanyError] = useState<string | null>(null)
  const [companyCode, setCompanyCode] = useState('')
  const [mode, setMode] = useState<SearchMode>('serialNumber')
  const [searchValue, setSearchValue] = useState('')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [results, setResults] = useState<SerialNumberCheck[]>([])
  const [queryLoading, setQueryLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [emptyMessage, setEmptyMessage] = useState<string | null>(null)

  const loadCompanies = useCallback(async () => {
    setCompaniesLoading(true)
    setCompanyError(null)
    try {
      const response = await new ChatCompanyListApi().list()
      const availableCompanies = normalizeCompanies(response)
      setCompanies(availableCompanies)
      if (availableCompanies.length === 1) setCompanyCode(availableCompanies[0].code)
      if (availableCompanies.length === 0) {
        setCompanyError('Sorgu yapabileceğiniz bir şirket bulunamadı.')
      }
    } catch (requestError: unknown) {
      setCompanies([])
      setCompanyError(getChatErrorMessage(requestError, 'Şirket listesi şu anda alınamıyor.'))
    } finally {
      setCompaniesLoading(false)
    }
  }, [])

  useEffect(() => {
    loadCompanies()
  }, [loadCompanies])

  const handleModeChange = (nextMode: SearchMode) => {
    setMode(nextMode)
    setSearchValue('')
    setStartDate('')
    setEndDate('')
    setResults([])
    setError(null)
    setEmptyMessage(null)
  }

  const validateAndBuildParameters = (): Record<string, string> | null => {
    if (!companyCode) {
      setError('Şirket seçimi zorunludur.')
      return null
    }

    const parameters: Record<string, string> = { companyCode }
    if (mode === 'dateRange') {
      const hasStartDate = Boolean(startDate)
      const hasEndDate = Boolean(endDate)
      if (hasStartDate !== hasEndDate) {
        setError('Başlangıç ve bitiş tarihlerini birlikte seçin.')
        return null
      }
      if (hasStartDate && startDate > endDate) {
        setError('Başlangıç tarihi bitiş tarihinden sonra olamaz.')
        return null
      }
      if (hasStartDate && hasEndDate) {
        parameters.startDate = startDate
        parameters.endDate = endDate
      }
    } else if (searchValue.trim()) {
      parameters[mode] = searchValue.trim()
    }

    return parameters
  }

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    if (queryLoading || companiesLoading) return

    setError(null)
    setEmptyMessage(null)
    const buttonParameters = validateAndBuildParameters()
    if (!buttonParameters) return

    setResults([])
    setQueryLoading(true)
    try {
      const response = await onRequest({
        buttonIntent: 'getSeriNoKontrol',
        buttonParameters,
      })
      const serialChecks = response.serialNumberChecks ?? []
      setResults(serialChecks)
      if (serialChecks.length === 0) {
        setEmptyMessage(response.text || 'Seçtiğiniz ölçütlerle eşleşen kayıt bulunamadı.')
      }
    } catch (requestError: unknown) {
      setError(getChatErrorMessage(requestError, 'Seri no kontrolü şu anda yapılamıyor.'))
    } finally {
      setQueryLoading(false)
    }
  }

  const selectedMode = SEARCH_MODES.find(option => option.key === mode)
  const busy = companiesLoading || queryLoading

  return (
    <FeatureModal
      titleId='serial-number-modal-title'
      title='Seri No Kontrol'
      description='Şirketinizi ve birbirini dışlayan arama ölçütlerinden birini seçin.'
      onClose={onClose}
      busy={busy}
    >
      {companiesLoading && <ModalLoading label='Şirketler yükleniyor…' />}
      {companyError && (
        <ModalFeedback tone='error'>
          <span>{companyError}</span>
          <button type='button' className={classes.modalFeedbackAction} onClick={loadCompanies}>
            Tekrar dene
          </button>
        </ModalFeedback>
      )}

      {!companiesLoading && companies.length > 0 && (
        <form className={classes.modalQueryForm} onSubmit={handleSubmit} noValidate>
          <label className={classes.modalField}>
            <span className={classes.modalFieldLabel}>Şirket</span>
            <select
              data-modal-initial-focus
              className={classes.modalSelect}
              value={companyCode}
              onChange={event => {
                setCompanyCode(event.target.value)
                setError(null)
              }}
              disabled={queryLoading}
            >
              <option value=''>Şirket seçiniz</option>
              {companies.map(company => (
                <option key={company.code} value={company.code}>
                  {company.name} ({company.code})
                </option>
              ))}
            </select>
          </label>

          <fieldset className={classes.modalModeFieldset}>
            <legend>Arama modu</legend>
            <div className={classes.modalModeTabs}>
              {SEARCH_MODES.map(option => (
                <button
                  key={option.key}
                  type='button'
                  className={`${classes.modalModeTab} ${mode === option.key ? classes.modalModeTabActive : ''}`}
                  onClick={() => handleModeChange(option.key)}
                  aria-pressed={mode === option.key}
                  disabled={queryLoading}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </fieldset>

          {mode === 'dateRange' ? (
            <div className={classes.modalDateGrid}>
              <label className={classes.modalField}>
                <span className={classes.modalFieldLabel}>Başlangıç tarihi</span>
                <input
                  className={classes.modalInput}
                  type='date'
                  value={startDate}
                  max={endDate || undefined}
                  onChange={event => {
                    setStartDate(event.target.value)
                    setError(null)
                  }}
                  disabled={queryLoading}
                />
              </label>
              <label className={classes.modalField}>
                <span className={classes.modalFieldLabel}>Bitiş tarihi</span>
                <input
                  className={classes.modalInput}
                  type='date'
                  value={endDate}
                  min={startDate || undefined}
                  onChange={event => {
                    setEndDate(event.target.value)
                    setError(null)
                  }}
                  disabled={queryLoading}
                />
              </label>
            </div>
          ) : (
            <label className={classes.modalField}>
              <span className={classes.modalFieldLabel}>{selectedMode?.label}</span>
              <input
                className={classes.modalInput}
                value={searchValue}
                onChange={event => {
                  setSearchValue(event.target.value)
                  setError(null)
                }}
                placeholder={selectedMode?.placeholder}
                autoComplete='off'
                disabled={queryLoading}
              />
            </label>
          )}

          <p className={classes.modalFormHint}>
            Arama değeri girmeden yalnızca şirket seçerseniz son 10 gün sorgulanır.
          </p>

          <button type='submit' className={classes.modalPrimaryButton} disabled={busy || !companyCode}>
            Seri numaralarını sorgula
          </button>
        </form>
      )}

      {queryLoading && <ModalLoading label='Seri numarası kayıtları sorgulanıyor…' />}
      {error && <ModalFeedback tone='error'>{error}</ModalFeedback>}
      {emptyMessage && <ModalFeedback tone='empty'>{emptyMessage}</ModalFeedback>}

      {results.length > 0 && (
        <section className={classes.modalSection} aria-labelledby='serial-results-title'>
          <div className={classes.modalSectionHeading}>
            <h3 id='serial-results-title'>Seri no sonuçları</h3>
            <span>{results.length} kayıt</span>
          </div>
          <div className={classes.modalTableWrap} tabIndex={0} aria-label='Seri no sonuç tablosu'>
            <table className={classes.modalTable}>
              <thead>
                <tr>
                  <th scope='col'>Seri no</th>
                  <th scope='col'>Ürün</th>
                  <th scope='col'>Marka / kategori</th>
                  <th scope='col'>Fatura</th>
                  <th scope='col'>E-fatura</th>
                  <th scope='col'>Fatura tarihi</th>
                  <th scope='col'>Teslimat</th>
                </tr>
              </thead>
              <tbody>
                {results.map((result, index) => (
                  <tr key={`${result.serialNumber || 'serial'}-${result.invoiceNumber || index}-${index}`}>
                    <td>{displayValue(result.serialNumber)}</td>
                    <td>
                      <strong>{displayValue(result.productName)}</strong>
                      <span>{displayValue(result.productCode)}</span>
                    </td>
                    <td>
                      <strong>{displayValue(result.brand)}</strong>
                      <span>{displayValue(result.category)}</span>
                    </td>
                    <td>{displayValue(result.invoiceNumber)}</td>
                    <td>{displayValue(result.eInvoiceNumber)}</td>
                    <td>{formatChatDate(result.invoiceDate)}</td>
                    <td>
                      <strong>{displayValue(result.deliveryNumber)}</strong>
                      <span>{formatChatDate(result.deliveryCreationDate)}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </FeatureModal>
  )
}
